<template>
  <div class="mb-4">
    <h5>{{ bundleItem.name }}</h5>
    <div class="row" :class="!showSizeSelector ? 'items-center' : null">
      <div v-if="previewImage" class="col-3">
        <img :src="formatUrl(previewImage)" />
      </div>
      <div :class="previewImage ? 'col-9' : 'col-12'">
        <p v-if="showColourSelector" class="mb-1">
          {{ activeColour ? 'Colour:' : 'Select colour:' }}
          <b v-if="activeColour">{{ activeColour.value }}</b>
        </p>
        <tml-new-product-color-selector
          v-if="showColourSelector && useDropdownColourSelect"
          v-model="activeColour"
          class="mb-4"
          :field-name="`color-bundle-${bundleItem.id}-${index}`"
          :colours="availableColours"
          :validation-rules="{required: true}"
          @input="emitBundleItem"
        />
        <tml-color-selector
          v-if="showColourSelector && !useDropdownColourSelect"
          :field-name="`color-bundle-${bundleItem.id}-${index}`"
          :colors="availableColours"
          :validation-rules="{required: true}"
          :pre-selected="preSelectedColour"
          :items-per-row="{
            xs: 4,
            sm:
              theme().get('productPage.color.selector.border.radius') ===
              '999px'
                ? 5
                : 6,
            md: 3,
            lg:
              theme().get('productPage.color.selector.border.radius') ===
              '999px'
                ? 6
                : 4,
          }"
          :border-radius="
            theme().get('productPage.color.selector.border.radius')
          "
          @input="onColourSelect($event)"
        />

        <div v-if="showSizeSelector">
          <div class="grid grid-cols-2 mt-2">
            <p class="mb-0">
              {{ activeSize ? 'Size:' : 'Select size:' }}
              <b v-if="activeSize">{{ activeSize.value }}</b>
            </p>
            <p v-if="bundleItem.sizeChartId" class="mb-1 text-right">
              <tml-anchor
                class="!underline cursor-pointer plain"
                @click.stop="openSizeChart"
              >
                <font-awesome-icon :icon="faRulerHorizontal" class="mr-1" />
                Size guide
              </tml-anchor>
            </p>
          </div>
          <tml-size-selector
            class="mb-2"
            :value="activeSize"
            :field-name="`size-${bundleItem.id}-${index}`"
            :sizes="availableSizes"
            :validation-rules="{required: true}"
            :items-per-row="{
              xs: 4,
              sm: 6,
              md: 3,
              lg: 4,
            }"
            @input="onSizeSelect($event)"
          />
        </div>
        <div v-if="shouldShowDispatchDays" class="mt-2">
          <font-awesome-icon class="mr-2" :icon="faClock" />
          <span>Ships out in {{ dispatchDays }} days</span>
        </div>
        <product-stock
          v-if="currentStock === 0"
          :class="{
            'mb-4': !useDropdownColourSelect,
            'mt-4': useDropdownColourSelect,
          }"
          :stock="currentStock"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {pickBy} from 'lodash';
import {useGtm} from '@teemill/common/services';
import {formatUrl} from '@teemill/common/helpers';
import {splitTests} from '@teemill/common/classes';
import ProductStock from './ProductStock.vue';

import {faRulerHorizontal} from '@fortawesome/pro-light-svg-icons/faRulerHorizontal';
import {faClock} from '@fortawesome/pro-light-svg-icons/faClock';

export default {
  name: 'BundleItemListItem',

  components: {
    ProductStock,
  },

  inject: ['theme'],

  props: {
    bundleItem: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },

    stock: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      activeColour: null,
      activeSize: null,

      faRulerHorizontal,
      faClock,
    };
  },

  computed: {
    useDropdownColourSelect() {
      return splitTests.isActiveVariation('product_colour_select', 'dropdown');
    },

    showSizeSelector() {
      return this.availableSizes?.length > 1 || this.bundleItem.sizeCount > 1;
    },

    showColourSelector() {
      return this.availableColours.length > 1;
    },

    sizePreSelected() {
      return this.showSizeSelector ? -1 : 0;
    },

    previewImage() {
      if (
        !this.activeColour ||
        !this.bundleItem.getImageByColour(this.activeColour.value)
      ) {
        return this.bundleItem.image?.size?.(120);
      }

      return this.bundleItem
        .getImageByColour(this.activeColour.value)
        ?.size(120);
    },

    availableColours() {
      return Object.values(this.bundleItem.options).map(
        ({colour, thumbnail}) => ({
          text: colour,
          value: colour,
          background: thumbnail ? this.colorBackground(thumbnail) : undefined,
        })
      );
    },

    preSelectedColour() {
      if (this.availableColours.includes(this.bundleItem.defaultColour)) {
        return this.bundleItem.defaultColour;
      }

      return this.availableColours[0];
    },

    stockableSizes() {
      const colour = this.activeColour?.value;

      const colourStock = this.stock?.[colour];

      if (colourStock === undefined) {
        return null;
      }

      return Object.keys(colourStock.sizes);
    },

    availableSizes() {
      const options = [];
      let sizes = [];

      if (Object.values(this.bundleItem.options).length) {
        const selectedOption =
          this.bundleItem.options[this.activeColour?.value] ||
          Object.values(this.bundleItem.options)[0];

        const filteredSizes = pickBy(
          selectedOption.sizes,
          size => size.price > 0
        );

        sizes = Object.keys(filteredSizes);
      }

      sizes.forEach(size => {
        if (!this.stockableSizes || this.stockableSizes.includes(size)) {
          options.push({
            text: size,
            value: size,
          });
        }
      });

      return options;
    },

    currentStock() {
      if (this.activeColour && this.activeSize?.value) {
        if (this.stock && this.stock[this.activeColour.value]) {
          return parseInt(
            this.stock[this.activeColour.value].sizes[this.activeSize.value]
              ?.stock?.quantity || 0,
            10
          );
        }
      }

      return null;
    },

    colourValue() {
      return this.activeColour?.value;
    },

    dispatchDays() {
      if (!this.activeColour?.value || !this.activeSize?.value) {
        return 0;
      }

      const stockData =
        this.stock?.[this.activeColour.value]?.sizes?.[this.activeSize.value]
          ?.stock;

      return stockData?.dispatchDays ?? 0;
    },

    shouldShowDispatchDays() {
      if (this.dispatchDays <= 0) {
        return false;
      }

      const hasSelectedColour = Boolean(this.activeColour?.value);

      if (!hasSelectedColour) {
        return false;
      }

      const hasSelectedSize = this.showSizeSelector
        ? Boolean(this.activeSize?.value)
        : true;

      if (!hasSelectedSize) {
        return false;
      }

      return true;
    },
  },

  watch: {
    activeSize: {
      immediate: true,
      handler() {
        this.emitBundleItem();
      },
    },

    colourValue() {
      this.resetSizeIfUnavailableForSelectedColour();
    },

    showColourSelector: {
      immediate: true,
      handler() {
        if (!this.activeColour) {
          this.activeColour = this.availableColours[0];
        }
      },
    },

    availableSizes: {
      immediate: true,
      handler(value) {
        if (value && value.length === 1) {
          this.activeSize = this.availableSizes[0];
        }
      },
    },
  },

  methods: {
    formatUrl,

    openSizeChart() {
      useGtm().trackEvent({
        event: 'onOpenSizeChart',
      });

      this.$overlays.open(`size-popup-${this.bundleItem.id}`);
    },

    onColourSelect(colour) {
      this.activeColour = colour;

      this.emitBundleItem();
    },

    onSizeSelect(size) {
      this.activeSize = size;
    },

    colorBackground(thumbnail) {
      switch (thumbnail?.type) {
        case 'image':
          return `url(${formatUrl(thumbnail.value)})`;
        case 'color':
          return thumbnail.value;
        default:
          return undefined;
      }
    },

    emitBundleItem() {
      this.$emit('input', {
        name: this.bundleItem.name,
        colour: this.activeColour?.value,
        size: this.activeSize?.value,
        id: this.bundleItem.id,
        stock: this.currentStock,
      });
    },

    resetSizeIfUnavailableForSelectedColour() {
      if (!this.activeSize) {
        return;
      }

      if (!this.stockableSizes) {
        this.activeSize = null;
        return;
      }

      if (!this.stockableSizes.includes(this.activeSize?.value)) {
        if (this.stockableSizes.length < 1) {
          this.activeSize = null;
        } else {
          const size = this.stockableSizes[this.stockableSizes.length - 1];

          this.activeSize = {
            value: size,
            text: size,
            name: size,
          };
        }
      }
    },
  },
};
</script>
